import useNotifications from '@/composables/useNotifications'
import { permissionScope, hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import { ref, computed, set } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import useCommon from "@/views/organization/useCommon"
import store from '@/store'
import { useLambda } from '@/views/habit/awsLambda'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import { flatPickrConfig } from '@/constants'
import { oid, date } from '@/helpers/mongoHelpers'
import useAuditLog from '@/composables/useAuditLog'

export default function useComplaintEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { formatDate, setState } = useCommon()
  const { updateItem, getItemsAggregate } = useLambda()
  const { sendEmailCase } = useCommonTodo()
  const { addAuditEntry } = useAuditLog()
  const complaintData = ref({})
  const isSubmitting = ref(false)
  const updateActiveTab = ref(false)
  const originalData = ref(null)
  const userData = store.state?.userStore?.userData
  const collection = 'complaint'
  const query = { _id: oid(route.value.params.id) }
  const startOfToday = new Date()
  const endOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  endOfToday.setHours(23, 59, 59, 999)
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const workerObjectId = oid(userData.worker_id?.$oid)

  const show = async (complaintId = null) => {
    isSubmitting.value = true

    try {
      // Capa 2: esta pantalla sirve tanto a Ver como a Editar — Editar siempre hereda el
      // scope de Ver, así que este mismo chequeo aplica a los dos casos.
      const restrictToOwn = permissionScope('complaint-channel', 'view') === 'own'

      // "actionPlanStages" no es un campo crudo del documento — solo existe una vez resuelto
      // vía $lookup (mismo bug ya encontrado y arreglado en useCaseEdit.js). Por eso este $or
      // no puede ir en el primer $match: en ese punto el documento crudo todavía no lo tiene,
      // así que esa condición nunca matcheaba y bloqueaba a cualquiera cuyo único vínculo con
      // la denuncia fuera ser executionResponsible de una etapa de plan de acción.
      const scopeMatch = restrictToOwn
        ? {
          $or: [
            { proposalResponsible: workerObjectId },
            { approvalResponsible: workerObjectId },
            { verificationResponsible: workerObjectId },
            { actionPlanStages: { $elemMatch: { executionResponsible: workerObjectId } } },
          ],
        }
        : {}

      const pipeline = [
        {
          $match: {
            ...(complaintId ? { _id: oid(complaintId) } : query),
            client_id: oid(userData.client.$oid),
            deleted: { $ne: true },
            // Same as the complaint list: never show a complaint to the person it was filed against
            reportedPerson: { $ne: workerObjectId },
          },
        },
        // Resuelve actionPlanStages ANTES del $match de scope de abajo, así "soy
        // executionResponsible de alguna etapa" es evaluable — mismo orden que ya funciona
        // en useCaseEdit.js/casesQueryWithOffset.
        {
          $lookup: {
            from: 'action_plan',
            localField: 'actionPlans',
            foreignField: '_id',
            pipeline: [ { $project: { _id: 0, executionResponsible: 1 } } ],
            as: 'actionPlanStages',
          },
        },
        { $match: scopeMatch },
        // Ya cumplió su función (evaluar el scope) — se saca para no dejarlo colgando en el
        // documento final; la vista usa el `actionPlans` resuelto más abajo, con más detalle.
        { $unset: 'actionPlanStages' },
        {
          $lookup: {
            from: 'worker',
            localField: 'proposalResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'proposalResponsible'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'approvalResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'approvalResponsible'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'verificationResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'verificationResponsible'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'reportedPerson',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'reportedPerson'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'reportedPersonResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'reportedPersonResponsible'
          }
        },
        {
          $lookup: {
            from: 'improvement',
            localField: 'associatedCases',
            foreignField: '_id',
            pipeline: [ { $project: { note: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'associatedCases'
          }
        },
        {
          $lookup: {
            from: 'location',
            localField: 'areas',
            foreignField: '_id',
            pipeline: [ { $project: { location: 1 } } ],
            as: 'areas'
          }
        },
        {
          $lookup: {
            from: 'company',
            localField: 'company',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } } ],
            as: 'company'
          }
        },
        {
          $lookup: {
            from: 'action_plan',
            localField: 'actionPlans',
            foreignField: '_id',
            pipeline: [
              {
                $lookup: {
                  from: 'worker',
                  localField: 'executionResponsible',
                  foreignField: '_id',
                  pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                  as: 'executionResponsible'
                }
              },
              {
                $lookup: {
                  from: 'worker',
                  localField: 'lastModifiedBy',
                  foreignField: '_id',
                  pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                  as: 'lastModifiedBy'
                }
              },
              { $addFields: { _id: { $toString: "$_id" }, executionResponsible: { $arrayElemAt: ["$executionResponsible", 0] }, lastModifiedBy: { $arrayElemAt: ["$lastModifiedBy", 0] } } },
            ],
            as: 'actionPlans'
          }
        },
        { $addFields: { _id: { $toString: "$_id" }, assignee: { $arrayElemAt: ["$assignee", 0] }, proposalResponsible: { $arrayElemAt: ["$proposalResponsible", 0] }, approvalResponsible: { $arrayElemAt: ["$approvalResponsible", 0] }, reportedPersonResponsible: { $arrayElemAt: ["$reportedPersonResponsible", 0] }, company: { $arrayElemAt: ["$company", 0] } } },
      ]
    
      const items = await getItemsAggregate({ collection, pipeline })

      if (!items.length) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        if (!complaintId) router.push({ name: 'organization-complaint-list' })
        return
      }

      const c = items[0] || {}

      // Sort action plans by origin index
      c.actionPlans?.sort((a, b) => a.originIndex - b.originIndex)

      // When the function comes with complaintId parameter, it is because it will be used to print to PDF
      complaintData.value = {
        ...c,
        themesComputed: computed(() => c.themes?.map(theme => theme.isNewOption ? theme.value : i18n.t(`complaintThemes.${theme.value}`)).join(" / ")),
        companyRelationshipComputed: c.companyRelationship ? computed(() => c.companyRelationship.isNewOption ? c.companyRelationship.value : i18n.t(`companyRelationships.${c.companyRelationship.value}`)) : '',
        reportedPerson: complaintId ? c.reportedPerson?.map(e => e.name) || [] : c.reportedPerson?.map(e => e._id) || [],
        reportedPersonResponsible: complaintId ? c.reportedPersonResponsible?.name : c.reportedPersonResponsible?._id,
        proposalResponsible: complaintId ? c.proposalResponsible?.name : c.proposalResponsible?._id,
        approvalResponsible: complaintId ? c.approvalResponsible?.name : c.approvalResponsible?._id,
        verificationResponsible: complaintId ? c.verificationResponsible?.map(e => e.name) || [] : c.verificationResponsible?.map(e => e._id) || [],
        associatedCases: complaintId ? c.associatedCases?.map(e => e.note) || [] : c.associatedCases?.map(e => e._id) || [],
        areas: complaintId ? c.areas?.map(e => e.location) || [] : c.areas?.map(e => e._id) || [],
        company: complaintId ? c.company?.name : c.company?._id,
        actionPlans: c.actionPlans?.map(e => ({
          ...e,
          executionResponsible: complaintId ? e.executionResponsible?.name : e.executionResponsible?._id,
          startDateConfig: {
            ...flatPickrConfig,
            maxDate: e.type === 'immediate' ? endOfToday : null,
          },
          endDateConfig: {
            ...flatPickrConfig,
            minDate: e.startDate || null,
          },
          lastModifiedDate: e.lastModifiedDate ? `${(new Date(e.lastModifiedDate)).toLocaleDateString(`${default_language || 'en'}-US`, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}hs` : null,
        })) || [],
        state: setState(c, startOfToday)
      }

      // Set reactive properties
      complaintData.value.actionPlans.forEach(e => {
        set(e, 'approvalResult', e.approvalResult)
        set(e, 'correctiveResult', e.correctiveResult)
        set(e, 'verificationResult', e.verificationResult)
        set(e, 'implementationResult', e.implementationResult)
        set(e, 'finalizedResult', e.finalizedResult)
        set(e, 'approvalNextStage', e.stage === 'analysis' || e.stage === 'corrective' || e.stage === 'verification' ? e.stage : '')
        set(e, 'correctiveNextStage', e.stage === 'analysis' || e.stage === 'verification' ? e.stage : '')
        set(e, 'verificationNextStage', e.stage === 'analysis' || e.stage === 'corrective' || e.stage === 'end' ? e.stage : '')
        set(e, 'implementationNextStage', e.stage === 'correctiveMeasures' || e.stage === 'finalized' ? e.stage : '')
        set(e, 'finalizedNextStage', e.stage === 'correctiveMeasures' || e.stage === 'implementation' || e.stage === 'end' ? e.stage : '')
        set(e, 'evidenceFile', [])
      })

      // Inicializar history si no existe (documentos legacy)
      if (!items[0].history) {
        items[0].history = []
        c.history = []
      }

      originalData.value = JSON.parse(JSON.stringify(items[0]))
      updateActiveTab.value = true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.complaint_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  const update = async (complaintData) => {
    if (!hasPermission('complaint-channel', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    if (complaintData.actionPlansToDelete?.length) deleteActionPlans(complaintData.actionPlansToDelete)

    try {
      const payload = {
        associatedCases: complaintData.associatedCases ? complaintData.associatedCases.map(e => oid(e)) : null,
        proposalResponsible: complaintData.proposalResponsible ? oid(complaintData.proposalResponsible) : null,
        approvalResponsible: complaintData.approvalResponsible ? oid(complaintData.approvalResponsible) : null,
        verificationResponsible: complaintData.verificationResponsible ? complaintData.verificationResponsible.map(e => oid(e)) : null,
        approvalDueDate: complaintData.approvalDueDate ? date(formatDate(complaintData.approvalDueDate)) : null,
        verificationDueDate: complaintData.verificationDueDate ? date(formatDate(complaintData.verificationDueDate)) : null,
        approvalComment: complaintData.approvalComment || '',
        verificationComment: complaintData.verificationComment || '',
        problemStatement: complaintData.problemStatement || '',
        why1: complaintData.why1 || '',
        why2: complaintData.why2 || '',
        why3: complaintData.why3 || '',
        why4: complaintData.why4 || '',
        why5: complaintData.why5 || '',
        actionPlans: complaintData.actionPlansIds?.map(e => oid(e)) || [],
        lastModifiedBy: userData?.worker_id?.$oid ? oid(userData.worker_id.$oid) : null,
        reportConclusion: complaintData.reportConclusion || '',
        why6: complaintData.why6 || '',
        why7: complaintData.why7 || '',
        why8: complaintData.why8 || '',
        why9: complaintData.why9 || '',
        why10: complaintData.why10 || '',
        finalizedComment: complaintData.finalizedComment || '',
        rootCause: complaintData.rootCause || 100,
        rootCauseCorrectiveMeasures: complaintData.rootCauseCorrectiveMeasures || 100,
      }
  
      // If complaint is in 'registration' stage then set stage as 'analysis'
      // If complaint is in 'analysis' stage and there are action plans then set stage as 'approval'
      // If complaint is in 'approval' stage and all action plans are in stage 'corrective/verification' then set stage as 'corrective' if there is at least one action plan in term, otherwise set stage as 'verification'
      // If complaint is in 'corrective' stage and all action plans are in stage 'verification' then set stage as 'verification'
      // If complaint is in 'verification' stage and all action plans are in stage 'end' then set stage as 'correctiveMeasures' for Karin Law and 'end' for Economic Crimes Law / Ethical Situations
      // If complaint is in 'correctiveMeasures' stage and there are action plans that were created in this stage then set stage as 'implementation' if there is at least one action plan in term, otherwise set stage as 'finalized'
      // If complaint is in 'implementation' stage and all action plans created in 'correctiveMeasures' are in stage 'finalized' then set stage as 'finalized'
      // If complaint is in 'finalized' stage and all action plans created in 'correctiveMeasures' are in stage 'end' then set stage as 'end'
      payload.stage = complaintData.stage === 'registration'
        ? 'analysis'
        : complaintData.stage === 'analysis' && complaintData.actionPlansIds?.length
          ? 'approval'
          : complaintData.stage === 'approval' && complaintData.actionPlans.some(e => e.stage === 'corrective' || e.approvalNextStage === 'corrective' || e.stage === 'verification' || e.approvalNextStage === 'verification')
            ? complaintData.actionPlans.some(e => e.stage === 'verification' || e.approvalNextStage === 'verification')
              ? 'verification'
              : 'corrective'
            : complaintData.stage === 'corrective' && complaintData.actionPlans.some(e => e.stage === 'verification' || e.correctiveNextStage === 'verification')
              ? 'verification'
              : complaintData.stage === 'verification' && complaintData.actionPlans.every(e => e.verificationNextStage === 'end')
                ? complaintData.type === 'karinLaw'
                  ? 'correctiveMeasures'
                  : 'end'
                : complaintData.stage === 'correctiveMeasures' && complaintData.correctiveMeasuresPlans?.length
                  ? complaintData.correctiveMeasuresPlans.some(e => e.type === 'immediate')
                    ? 'finalized'
                    : 'implementation'
                  : complaintData.stage === 'implementation' && complaintData.actionPlans.some(e => e.stage === 'finalized' || e.implementationNextStage === 'finalized')
                    ? 'finalized'
                    : complaintData.stage === 'finalized' && complaintData.actionPlans.every(e => e.stageCreated === 'analysis' || e.finalizedNextStage === 'end')
                      ? 'end'
                      : complaintData.stage
  
      // If complaint is being set to 'end' (Economic Crimes Law / Ethical Situations) or 'correctiveMeasures' (Karin Law) check if it is completed overdue
      if (complaintData.stage === 'verification' && complaintData.actionPlans.every(e => e.verificationNextStage === 'end') && payload.verificationDueDate < startOfToday) {
        payload.isCompletedOverdue = true
      }

      const action = { $set: payload, $push: { allFileKeys: { $each: complaintData.newEvidenceKeys || [] } } }

      await updateItem({ collection, query, action })

      // Add audit log entry
      await addAuditEntry({
        collection,
        documentId: oid(route.value.params.id),
        originalData: originalData.value,
        updatedData: payload,
      })

      showSuccessMessage(i18n.t('message.complaint_updated'))

      const originalStage = complaintData.stage
      complaintData.stage = payload.stage

      // If case passed to stage 'analysis' send email to proposal responsible with the details of the case updated
      if (complaintData.proposalResponsible && originalStage !== 'analysis' && payload.stage === 'analysis') {
        sendEmailCase(complaintData, "update", 'proposalResponsible', 'complaint')
      }

      // If case passed to stage 'approval' send email to approval responsible with the details of the case updated
      else if (complaintData.approvalResponsible && originalStage !== 'approval' && payload.stage === 'approval') {
        sendEmailCase(complaintData, "update", 'approvalResponsible', 'complaint')
      }

      // If case passed to stage 'verification' send email to verification responsible with the details of the case updated
      else if (complaintData.verificationResponsible?.length && originalStage !== 'verification' && payload.stage === 'verification') {
        sendEmailCase(complaintData, "update", 'verificationResponsible', 'complaint')
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.complaint_update_error'))
    } finally {
      router.push({ name: 'organization-complaint-list' })
    }
  }

  const addNewMessage = (message) => {
    try {
      const action = { $push: { messages: message, allFileKeys: { $each: message.evidenceKey || [] } } }
      updateItem({ collection, query, action })
    } catch (error) {
      console.log(error)
    }
  }

  const deleteActionPlans = (ids) => {
    ids.forEach(async (e) => {
      try {
        const query = { _id: oid(e) }
        const payload = { deleted: true }
        const action = { $set: payload }

        await updateItem({ collection: 'action_plan', query, action })
      } catch (error) {
        console.log(error)
      }
    })
  }

  return {
    update,
    show,
    complaintData,
    isSubmitting,
    updateActiveTab,
    addNewMessage,
  }
}
